import { interpolateRgbBasis, type ScaleLinear } from "d3";
import { is, mean, repeat, update } from "ramda";

import {
  type BlastHit,
  type BlastHsp,
  type DataRow,
  type Feature,
  type Range,
  type Region,
  type RegionFlag,
  type StringMapEntry,
  type TrackExpressionColoring,
  type Variant,
} from "../../graphql.js";
import { type IGenericVariant } from "../../redux/modules/genome-browser.js";
import {
  EXPRESSION_LANE_HEIGHT,
  MIN_PROFILE_TOP_SPACE,
  STRAND_TYPE,
  type StrandType,
  TRACK_TYPE,
} from "../../settings/settings.js";
import { getHitName, getHitQueryRange, getHspHitRange, getHspName, getHspQueryRange } from "../blast.js";
import { rangesIntersect } from "../range.js";
import {
  aaRectFeatureHeight,
  aaSeqFeatureHeight,
  MIN_PIXEL_PER_BASE_AA_BOX,
  MIN_PIXEL_PER_BASE_AA_LETTER,
  variantShapePileupParams,
} from "./shapes-settings.js";

export interface IPileup {
  features?: IPileupFeature[];
  variants?: IPileupVariant[];
  hsps?: IPileupHsp[];
  hits?: IPileupHit[];
  flags?: IPileupFlag[];
  hiddenCount?: number;
  lanes: number;
  pileupParams: IPileupParams;
  expressionLaneNumber?: number;
}

export interface IPileupParams {
  featureHeight: number;
  lanePadding: number;
  labelHeight: number; // if null then the label is included in the shape size, otherwise its located on top of the lane
  minShapeWidth: number;
  charWidthInPixel: number;
  maxLanes?: number;
  boxHeight?: number;
  hideLabels?: boolean;
  showSeqBoxes?: boolean;
  showSeqLetters?: boolean;
  canvasWidth?: number;
}

export interface IPileupElement {
  x: number;
  y: number;
  width: number;
  lane: number;
}

export interface IExpressionParams {
  condition?: string;
  expressionColor?: string;
  expressionValue?: number;
  isExpressionColoring?: boolean;
}

export interface IPileupFeature extends IPileupElement {
  mappings: IPileupFeatureMapping[];
  feature: Feature;
  startCut: boolean;
  endCut: boolean;
  expressionParams?: IExpressionParams[];
}

export interface IPileupGenericFeature extends IPileupElement {
  mappings: IPileupFeatureMapping[];
  feature: Feature | RegionFlag;
  startCut: boolean;
  endCut: boolean;
  expressionParams?: IExpressionParams[];
  midpoint: number;
}

export interface IPileupFeatureMapping {
  x: number;
  width: number;
  start: number;
  stop: number;
}

export interface IPileupVariant extends IPileupElement {
  midpoint: number;
  variants: IGenericVariant[];
  profileMidpoints?: number[];
  profileOffset?: number;
  profileWidth?: number;
}

export interface IPileupFlag extends IPileupElement {
  flag: RegionFlag;
  midpoint: number;
  startCut: boolean;
  endCut: boolean;
}
export interface IPileupHit extends IPileupElement {
  hit: BlastHit;
  mappings: IPileupHspMapping[];
}

export interface IPileupHsp extends IPileupElement {
  hit: BlastHit;
  hsp: BlastHsp;
  hitStrand: StrandType;
  queryStrand: StrandType;
  startCut: boolean;
  endCut: boolean;
}

export interface IPileupHspMapping extends IPileupFeatureMapping {
  hsp: BlastHsp;
  hitStrand: StrandType;
  queryStrand: StrandType;
}

export interface ILaneInfo {
  nextFree: number;
  indexLookup: number[];
}

interface IDrawingParameter {
  x: number;
  x1: number;
  x2: number;
  midpoint: number;
  width: number;
}

interface IVariantInfo extends IDrawingParameter {
  variants: IGenericVariant[];
  laneIndex?: number;
  pileupPos?: number; // index in pileupVariants
}

interface IProtoPileup {
  groups: IVariantInfo[];
  indexLookup: number[]; // array with indices into groups at a position on screen
  nextFree: number;
}

type IProtoPileupByType = Record<string, IProtoPileup>;

type IVariantByType = Record<string, IVariantInfo[]>;

interface ICollisionGroup extends IDrawingParameter {
  varByType: IVariantByType;
  groupIndex: number;
}

interface IPileupData {
  lanes: ILaneInfo[];
  pileupVariants: IPileupVariant[];
  piledCount: number;
}

interface IGroupFingerprint extends IDrawingParameter {
  type: string;
  groupIndex: number;
  count: number;
}

export function estimatePileupCapacity(canvasWidth: number, pileupParams: IPileupParams) {
  return Math.round((canvasWidth / pileupParams.featureHeight) * pileupParams.maxLanes * 0.8);
}

/**
 * A factory class that calculates a pileup of genomic features.
 *
 * In the pileup, each genomic feature is assigned view coordinates and a width so that the features down overlap with
 * each other. Features that would occupy the same view coordinates are assigned to separate lanes, which in turn leads
 * to the top-down piling up of elements. The factory accounts for the fact that the label of genomic features will
 * also occupy screen space to the left of the actual shape if the shape is too small to hold the label text.
 *
 * created on 11/30/2016
 */
export class PileupFactory {
  private readonly scale: ScaleLinear<number, number>;

  constructor(scale: ScaleLinear<number, number>) {
    this.scale = scale;
  }

  buildFeaturePileup(
    features: Feature[],
    currentRange: Range,
    pileupParams: IPileupParams,
    showAll: boolean,
    expressionLaneNumber: number,
  ): IPileup {
    // Sort by start then longest range
    const sortedFeatures: Feature[] = features.slice(0).sort(compareByStartThenLongestFirst);

    const pileup = this.buildFeaturePileupImpl(
      sortedFeatures,
      currentRange,
      pileupParams,
      pileupParams.hideLabels,
      showAll,
      expressionLaneNumber,
    );

    return {
      // translate the generic pileup feature to a feature-specific one
      features: pileup.pileupFeatures.map((f: IPileupGenericFeature) => {
        const { feature, midpoint, ...rest } = f;
        return { ...rest, feature: feature as Feature };
      }),
      lanes: pileup.lanes,
      pileupParams: { ...pileupParams, hideLabels: pileup.hideLabels },
      hiddenCount: pileup.hiddenCount,
      expressionLaneNumber: pileup.expressionLaneNumber,
    };
  }

  private buildFeaturePileupImpl(
    features: Feature[] | RegionFlag[],
    currentRange: Range,
    pileupParams: IPileupParams,
    hideLabels: boolean,
    showAll: boolean,
    expressionLaneNumber: number,
  ) {
    const pileupFeatures: IPileupGenericFeature[] = [];
    const lanes: number[] = [];
    let overflow = false;
    let hiddenCount = 0;
    let labelsTakesSpace = false;

    const expressionLaneHeight = EXPRESSION_LANE_HEIGHT * (expressionLaneNumber ?? 0);

    // for proteinLocation features, we need to decide here if we want to show the sequence boxes, or even the letters
    const firstFeature = features[0] as Feature;
    if (features.length > 0 && firstFeature.type === TRACK_TYPE.PROTEIN_LOCATION && firstFeature.mappings.length > 0) {
      const start: number = firstFeature.mappings[0].start;
      const stop: number = firstFeature.mappings[0].stop;
      const baseWidth: number = (this.scalePos(stop) - this.scalePos(start)) / (stop - start + 1);

      pileupParams.showSeqBoxes = baseWidth >= MIN_PIXEL_PER_BASE_AA_BOX;
      pileupParams.featureHeight = baseWidth >= MIN_PIXEL_PER_BASE_AA_BOX ? aaSeqFeatureHeight : aaRectFeatureHeight;

      pileupParams.showSeqLetters = baseWidth >= MIN_PIXEL_PER_BASE_AA_LETTER;
      pileupParams.canvasWidth = this.scalePos(currentRange.stop) - this.scalePos(currentRange.start) + 1;
    }

    // we use a for loop to be able to return (case of overflow)
    for (let fi = 0; fi < features.length; fi++) {
      const feature: Feature | RegionFlag = features[fi];
      const featureRange = feature.region.range;

      if (rangesIntersect(featureRange, currentRange)) {
        // we calculate the range we will draw the SVG elements to
        const featureRangeStart: number = Math.max(featureRange.start, currentRange.start);
        const featureRangeStop: number = Math.min(featureRange.stop, currentRange.stop);

        // give the information if the feature is cut so that we can adapt our drawing
        const startCut: boolean = featureRangeStart > featureRange.start;
        const endCut: boolean = featureRangeStop < featureRange.stop;

        // calculate the drawing parameters
        const x: number = Math.round(this.scalePos(featureRangeStart));
        const width: number = Math.max(
          Math.round(this.scalePos(featureRangeStop + 1) - this.scalePos(featureRangeStart)),
          pileupParams.minShapeWidth,
        );

        // to calculate the pileup
        // genome 'Acla' has features with name=null
        const name = feature.name || "";
        const labelWidth: number = hideLabels ? 0 : name.length * pileupParams.charWidthInPixel;
        const pileupLabelWidth: number = pileupParams.labelHeight > 0 || width > labelWidth ? 0 : labelWidth;
        if (!hideLabels && labelWidth > width) {
          labelsTakesSpace = true;
        }

        // if the label is on top , we choose the max of both shape and label
        // also, if the label is larger than the shape because of the canvas cut, we don't want the lanes to get messed up, we won't encounter the label
        const x2: number =
          pileupParams.labelHeight > 0 && (width > labelWidth || !startCut)
            ? Math.max(x + width, x + labelWidth)
            : x + width;

        // try to pileup the feature into an existing lane without collisions
        let lane = -1;

        for (let i = 0; i < lanes.length; i++) {
          if (x - pileupLabelWidth > lanes[i]) {
            lane = i;
            lanes[i] = x2;
            break;
          }
        }

        // no space for this feature, and we hit the configured max number of lanes
        if (lane < 0 && pileupParams.maxLanes && lanes.length >= pileupParams.maxLanes) {
          // If the features overflow beyond the configured max number of lanes with the labels (and because of them),
          // try a second pass without showing the labels
          if (!hideLabels && labelsTakesSpace) {
            return this.buildFeaturePileupImpl(
              features,
              currentRange,
              pileupParams,
              true,
              showAll,
              expressionLaneNumber,
            );
          }
        }

        // ...otherwise create a new lane when conditions are met
        if (lane < 0 && (!pileupParams.maxLanes || lanes.length < pileupParams.maxLanes || showAll)) {
          lanes.push(x2);
          lane = lanes.length - 1;
        }

        if (lane !== -1) {
          // calculate y coordinate based on lane
          const y: number =
            lane * (pileupParams.featureHeight + expressionLaneHeight + pileupParams.lanePadding) +
            (lane + 1) * pileupParams.labelHeight;

          // scale the mapping locations
          let mappings: IPileupFeatureMapping[] = [];
          const mappingFeature = feature as Feature;
          if (mappingFeature.mappings && mappingFeature.mappings.length > 0) {
            mappings = mappingFeature.mappings.map((mappingRange: Range) => {
              const mappingX: number = Math.round(this.scalePos(mappingRange.start)) - x;
              const mappingWidth: number = Math.max(
                Math.round(this.scalePos(mappingRange.stop + 1) - this.scalePos(mappingRange.start)),
                pileupParams.minShapeWidth,
              );
              return { x: mappingX, width: mappingWidth, start: mappingRange.start, stop: mappingRange.stop };
            });
          }

          // Required for Flags.
          // Midpoint is the actual middle point in pixel coordinates of the flag range irrespective of cropping
          // adding 0.5 to make the midpoint centered at actual base character instead of the axis ticks
          const midpoint: number = Math.round(
            this.scalePos((feature.region.range.start + feature.region.range.stop) / 2.0 + 0.5),
          );

          pileupFeatures.push({
            x,
            y,
            width,
            feature: feature,
            mappings,
            startCut,
            endCut,
            lane,
            expressionParams: [],
            midpoint,
          });
        } else {
          // Couldn't place feature because max lane count is exceeded
          overflow = true;
          hiddenCount++;
        }
      }
    }

    return {
      pileupFeatures,
      overflow,
      hiddenCount,
      lanes: lanes.length,
      expressionLaneNumber,
      hideLabels: hideLabels, // might be overwritten by the second pass
    };
  }

  buildVariantPileup(variants: IGenericVariant[], visualRange: Range, pileupParams: IPileupParams): IPileup {
    const canvasWidth: number = Math.round(this.scale.range()[1]);

    const pd: IPileupData = this.buildClusteredVariantPileUp(variants, visualRange, pileupParams);
    this.addVariantProfiles(canvasWidth, pileupParams, pd);

    return {
      variants: pd.pileupVariants.sort(compareByLaneThenStart),
      lanes: pd.lanes.length,
      pileupParams,
    };
  }

  // Don't merge variants unless their variant symbols overlap
  //              [....m....]
  //              ........[....m....]         overlaps -> merge or separate lanes
  //              ...........[....m....]      no overlap, but no 2 px distance, handled as above
  //                        ..[....m....]     no overlap + 2 px distance, independent, show next to each other
  private canMergeToGroup(
    candidateMidpoint: number,
    candidateVariantType: string,
    midpoint: number,
    variantType: string,
    symbolHalfWidth: number,
  ): boolean {
    // based on two different sets of values
    if (candidateVariantType !== variantType) {
      return false;
    }
    return Math.abs(candidateMidpoint - midpoint) <= 2 * symbolHalfWidth;
  }

  private isLaneUsed(group: IDrawingParameter, indexLookup: number[], threshold = -1): boolean {
    if (!indexLookup) {
      return false;
    }
    for (let i = group.x1; i < group.x2 + 1 && i < indexLookup.length; i++) {
      const value = indexLookup[i];
      if (value !== undefined && value > threshold) {
        return true;
      }
    }
    return false;
  }

  private calcVariantSymbolParameters(
    variant: IGenericVariant,
    canvasWidth: number,
    pileupParams: IPileupParams,
  ): IDrawingParameter {
    // calculate the drawing parameters, note that we need to add 1 to stop because it's inclusive
    const variantRange: Range = variant.region.range;
    const x: number = Math.round(this.scalePos(variantRange.start));
    const width: number = Math.max(Math.round(this.scalePos(variantRange.stop + 1)) - x, pileupParams.minShapeWidth);

    // midpoint is the actual middle point in pixel coordinates of the variant range irrespective of cropping
    // adding 0.5 to make the midpoint centered at actual base character instead of the axis ticks
    const midpoint: number = Math.round(this.scalePos((variantRange.start + variantRange.stop) / 2.0 + 0.5));

    const symbolHalfWidth: number = Math.round(pileupParams.featureHeight / 2);
    // x1 is actual variant range start or the leftmost pixel of the variant symbol
    const symbolX1 = midpoint - symbolHalfWidth;
    const x1: number = Math.max(Math.round(variant.alleleLength === 0 ? symbolX1 : Math.min(x, symbolX1)), 0);

    // x2 is actual variant range stop or the rightmost pixel of the variant symbol
    const symbolX2 = midpoint + symbolHalfWidth;
    const x2: number = Math.min(
      Math.round(variant.alleleLength === 0 ? symbolX2 : Math.max(x + width, symbolX2)),
      canvasWidth,
    );

    return { x, x1, x2, width, midpoint };
  }

  private buildClusteredVariantPileUp(
    variants: IGenericVariant[],
    visibleRange: Range,
    pileupParams: IPileupParams,
  ): IPileupData {
    const symbolHalfWidth: number = Math.round(pileupParams.featureHeight / 2);
    const canvasWidth: number = Math.round(this.scale.range()[1]);

    // Override the global maxLanes setting if it allows less than 10 lanes
    const localPileupParams: IPileupParams = { ...pileupParams, maxLanes: Math.max(pileupParams.maxLanes, 10) };

    // Goal:
    //    Show at least one lane for each present variant type in a collision group
    //    There should be only one numbered group per type in a collision group
    //    Show as many different variants as possible ( information is in the difference )
    //    If space and number of lanes allows expand the groups
    //    Use lanes from top to bottom, place new lanes on the highest free lane.
    //
    // Steps
    //  Merge  type pure collision groups  to  combined collision groups
    //  Assign combined collision groups to lanes
    //  Split  single variants from combined collision group if there is still space in the lanes

    // Steps
    //  Merge all variants with overlapping range and type.
    //  Build type pure collision groups.
    //
    const varByType: IProtoPileupByType = {};

    variants.forEach((variant: IGenericVariant) => {
      const variantRange: Range = variant.region.range;
      if (rangesIntersect(variantRange, visibleRange)) {
        if (!varByType[variant.variantType]) {
          varByType[variant.variantType] = {
            groups: [],
            indexLookup: new Array(canvasWidth),
            nextFree: 0,
          };
        }
        const variantLane: IProtoPileup = varByType[variant.variantType];
        const dp: IDrawingParameter = this.calcVariantSymbolParameters(variant, canvasWidth, pileupParams);
        // The variant (symbol) uses space from x1 to x2

        let groupIndex: number = variantLane.groups.length;
        // try to fit the variant into an existing lane without collisions and 2 pixels padding
        // find the group for the variant or create a new one
        const otherGroupIndex = variantLane.indexLookup[dp.x1];

        if (
          !(
            is(Number, otherGroupIndex) &&
            this.canMergeToGroup(
              variantLane.groups[otherGroupIndex].midpoint,
              variant.variantType,
              dp.midpoint,
              variant.variantType,
              symbolHalfWidth,
            )
          )
        ) {
          // Create a new group on typed lane
          // record the lane into which this variant is placed
          // record next free view coordinate in this lane
          variantLane.nextFree = dp.x2;
          // mark view coordinates in lane as reserved for this pileup element
          for (let i = dp.x1; i < dp.x2 + 1; i++) {
            variantLane.indexLookup = update(i, groupIndex, variantLane.indexLookup);
          }
          variantLane.groups[groupIndex] = { variants: [variant], ...dp };
        } else {
          // Merge into an existing group and lane
          groupIndex = variantLane.indexLookup[dp.x1];
          const otherGroup: IVariantInfo = variantLane.groups[groupIndex];
          otherGroup.variants.push(variant);

          if (dp.x2 > otherGroup.x2) {
            for (let i = dp.x1; i < dp.x2 + 1; i++) {
              variantLane.indexLookup = update(i, groupIndex, variantLane.indexLookup);
            }
            otherGroup.x2 = Math.max(dp.x2, otherGroup.x2);
            otherGroup.width = otherGroup.x2 - otherGroup.x1 + 1;
          }
        }
      }
    });

    // Build combined collision groups from type pure collision groups
    const collisionList: ICollisionGroup[] = this.buildCollisionGroupList(varByType, canvasWidth, localPileupParams);

    // Some proto pileups are built from two or more variants which do not overlap 100%
    //   sometimes each variant can be printed separately with vertical overlap by shifting one to a lower lane
    //   if there is enough space for variants.
    //   -> if there is some space split such proto pileups into two proto pileups

    return this.convertCollisionGroupsToPileup(collisionList, canvasWidth, localPileupParams);
  }

  private buildCollisionGroupList(
    varByType: IProtoPileupByType,
    canvasWidth: number,
    pileupParams: IPileupParams,
  ): ICollisionGroup[] {
    // Build a list of fingerprint records for all collision groups,
    //  and sort it by start asc and by stop asc.

    // Build the drawing parameters of all groups
    const groupList: IGroupFingerprint[] = [];
    for (const type of Object.keys(varByType)) {
      varByType[type].groups.forEach((g: IVariantInfo, groupIndex) => {
        const dp: IDrawingParameter = this.calcVariantSymbolParametersOfGroup(g, canvasWidth, pileupParams);
        groupList.push({ groupIndex, type: type, count: g.variants.length, ...dp });
      });
    }
    const orderedGroupList: IGroupFingerprint[] = groupList.sort(compareByIconStartAndByWidestFirstAndCount);

    // Iterate over the sorted list and merge the group to the  current collision group
    //  if the current group overlaps with the current collision group
    //  otherwise open a new collision group with the current group only.

    const collisionList: ICollisionGroup[] = [];
    let curr: IGroupFingerprint; // cursor over the locations in the visual range

    orderedGroupList.forEach((t: IGroupFingerprint, _tIndex: number) => {
      const group: IVariantInfo = varByType[t.type].groups[t.groupIndex];
      if (!!curr && isFingerprintOverlap(curr, t)) {
        // push group of t to collisionList[curr].groups
        //  assert: there can be more than one 'type pure collision group' per type to be merged into a 'combined collision group'.
        if (!collisionList[collisionList.length - 1].varByType[t.type]) {
          collisionList[collisionList.length - 1].varByType[t.type] = [group];
        } else {
          collisionList[collisionList.length - 1].varByType[t.type].push(group);
        }
      } else {
        // create a new curr && collision group
        curr = t;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { type, count, ...parms } = t; // strip type from params
        const c: ICollisionGroup = { varByType: {}, ...parms };
        c.varByType[t.type] = [group];
        collisionList.push(c);
      }
    });

    return collisionList;
  }

  private optionalIndexLookup(laneIndex: number, lanes: ILaneInfo[]) {
    return laneIndex < lanes.length ? lanes[laneIndex].indexLookup : null;
  }

  private placeVariantOnPilupLane(
    group: IVariantInfo,
    laneIndex: number,
    pd: IPileupData,
    pileupParams: IPileupParams,
  ) {
    pd.lanes[laneIndex].nextFree = Math.max(group.x2, pd.lanes[laneIndex].nextFree);
    for (let i = group.x1; i < group.x2; i++) {
      pd.lanes[laneIndex].indexLookup = update(i, pd.pileupVariants.length, pd.lanes[laneIndex].indexLookup);
    }
    //   _.map(group.variants, (v) => v.name).join());
    const y: number =
      laneIndex * (pileupParams.featureHeight + pileupParams.lanePadding) + (laneIndex + 1) * pileupParams.labelHeight;
    pd.pileupVariants.push({
      x: group.x,
      y: y,
      width: group.width,
      lane: laneIndex,
      midpoint: group.midpoint,
      variants: group.variants,
    });
    group.laneIndex = laneIndex;
    group.pileupPos = pd.pileupVariants.length - 1; // remember the index of the resulting pileup for recalculation of drawing features
    pd.piledCount += group.variants.length; // the number of variants
  }

  private convertCollisionGroupsToPileup(
    collisionList: ICollisionGroup[],
    canvasWidth: number,
    pileupParams: IPileupParams,
  ): IPileupData {
    //  placement of collision groups on lanes: assign all symbols in one group to a stack of lanes
    //    modifies the variant info groups in the proto pileups (set laneIndex)

    const pd: IPileupData = {
      lanes: [],
      pileupVariants: [],
      piledCount: 0,
    };

    //   extend the number of lanes
    for (let i = 0; i < collisionList.length; i++) {
      const c: ICollisionGroup = collisionList[i];
      let laneIndex = -1;

      for (const type of Object.keys(c.varByType)) {
        const protoPileup: IVariantInfo[] = c.varByType[type];
        laneIndex++;

        protoPileup.forEach((group: IVariantInfo, _gi: number) => {
          const dp: IDrawingParameter = this.calcVariantSymbolParametersOfGroup(group, canvasWidth, pileupParams);
          let isUsed: boolean = this.isLaneUsed(dp, this.optionalIndexLookup(laneIndex, pd.lanes));
          //
          while (laneIndex < pd.lanes.length && isUsed /* && group.x1 < pd.lanes[laneIndex].nextFree + 2 */) {
            isUsed = this.isLaneUsed(dp, this.optionalIndexLookup(laneIndex, pd.lanes));
            laneIndex++;
          }
          // open a new lane
          if (pd.lanes.length === laneIndex) {
            pd.lanes.push({ nextFree: 0, indexLookup: new Array(canvasWidth) });
          }

          this.updatePileupFromGroup(group, pd, canvasWidth, pileupParams);
          this.placeVariantOnPilupLane(group, laneIndex, pd, pileupParams);
        });
      }
    }

    return pd;
  }

  private updatePileupFromGroup(
    group: IVariantInfo,
    pd: IPileupData,
    canvasWidth: number,
    pileupParams: IPileupParams,
  ) {
    const data: IDrawingParameter = this.calcVariantSymbolParametersOfGroup(group, canvasWidth, pileupParams);
    group.x = data.x;
    group.x1 = data.x1;
    group.x2 = data.x2;
    group.width = data.width;
    group.midpoint = data.midpoint;
    /// adjust the parent pileup, reduce the width in the existing pileup group
    const pileup = pd.pileupVariants[group.pileupPos];
    if (group.pileupPos && pileup) {
      pileup.midpoint = data.midpoint;
      pileup.width = data.width;
      pileup.x = data.x;
    }
  }

  private calcVariantSymbolParametersOfGroup(
    group: IVariantInfo,
    canvasWidth: number,
    pileupParams: IPileupParams,
  ): IDrawingParameter {
    const variant = {
      alleleLength: 0,
      region: {
        range: {
          start: 1e18,
          stop: 0,
        },
      } as unknown as Region,
    } as unknown as Variant;

    group.variants.forEach((v) => {
      variant.region.range.start = Math.min(variant.region.range.start, v.region.range.start);
      variant.region.range.stop = Math.max(variant.region.range.stop, v.region.range.stop);
      variant.alleleLength = Math.max(variant.alleleLength, v.alleleLength);
    });

    return this.calcVariantSymbolParameters(variant, canvasWidth, pileupParams);
  }

  private addVariantProfiles(canvasWidth: number, pileupParams: IPileupParams, pd: IPileupData) {
    // calculate profileMidpoints for each variant pileup element
    const profileWidth: number = Math.min(
      variantShapePileupParams.boxHeight,
      Math.max(4, Math.round((0.5 * canvasWidth) / pd.piledCount)),
    );
    const halfProfileWidth: number = profileWidth / 2;

    let profilePos: number =
      pd.lanes.length * (pileupParams.featureHeight + pileupParams.lanePadding) + pileupParams.lanePadding;

    // We need to correct the profilePos for the min y distance between the track label and the mutant names.
    // Note that the expression "pileupParams.featureHeight/2" works but it may not be entirely correct.
    profilePos = Math.max(profilePos, MIN_PROFILE_TOP_SPACE + pileupParams.featureHeight / 2);

    const sortedPileupVariants: IPileupVariant[] = pd.pileupVariants.sort(compareByMidpointThenLaneThenStart);

    let i = 0;
    let maxProfileMidpoint = -1;
    while (i < sortedPileupVariants.length) {
      const vi: IPileupVariant = sortedPileupVariants[i];
      const clusterVariants: IPileupVariant[] = [vi];
      let variantCount: number = vi.variants.length;
      let avgMidpoint: number = vi.midpoint;
      let nextFree: number = vi.midpoint + halfProfileWidth;
      let j: number = i + 1;

      while (j < sortedPileupVariants.length) {
        const vj: IPileupVariant = sortedPileupVariants[j];

        if (vj.midpoint - halfProfileWidth <= nextFree) {
          clusterVariants.push(vj);
          nextFree = vj.midpoint + halfProfileWidth;
          avgMidpoint += vj.midpoint;
          variantCount += vj.variants.length;
          j++;
        } else {
          break;
        }
      }

      // advance cursor
      i = j;
      avgMidpoint /= clusterVariants.length;

      let profileMidpoint: number = Math.max(
        maxProfileMidpoint,
        avgMidpoint + ((variantCount - 1) * profileWidth) / -2,
      );

      for (let k = 0; k < clusterVariants.length; k++) {
        const vj: IPileupVariant = clusterVariants[k];

        vj.profileOffset = profilePos - vj.y;
        vj.profileWidth = profileWidth;
        vj.profileMidpoints = [];

        for (let l = 0; l < vj.variants.length; l++) {
          vj.profileMidpoints.push(profileMidpoint);
          profileMidpoint += profileWidth;
          maxProfileMidpoint = profileMidpoint;
        }
      }
    }
  }

  buildFlagPileup(flags: RegionFlag[], currentRange: Range, pileupParams: IPileupParams, showAll: boolean): IPileup {
    // Sort by start then longest range
    const sortedFlags: RegionFlag[] = flags.slice(0).sort(compareByStartThenLongestFirst);

    // do the standard feature pileup
    const pileup = this.buildFeaturePileupImpl(
      sortedFlags,
      currentRange,
      pileupParams,
      pileupParams.hideLabels,
      showAll,
      null,
    );

    // Crop all regions to the canvas and nullify the midpoint if it is outside the canvas.
    // This makes sure that the flag popover is always positioned correctly.
    const canvasWidth: number = Math.round(this.scale.range()[1]);
    const symbolWidth: number = pileupParams.featureHeight;
    pileup.pileupFeatures?.forEach((pileupFeature: IPileupGenericFeature) => {
      pileupFeature.x = pileupFeature.x < 0 ? 0 : pileupFeature.x;
      pileupFeature.width =
        pileupFeature.x + pileupFeature.width > canvasWidth ? canvasWidth - pileupFeature.x : pileupFeature.width;
      pileupFeature.midpoint =
        pileupFeature.midpoint < -symbolWidth || pileupFeature.midpoint > canvasWidth ? null : pileupFeature.midpoint;
    });

    return {
      // translate the generic pileup objects to the flag-specific ones
      flags: pileup.pileupFeatures.map((f: IPileupGenericFeature) => {
        const { feature, mappings, expressionParams, ...rest } = f;
        return { ...rest, flag: feature as RegionFlag };
      }),
      lanes: pileup.lanes,
      pileupParams: { ...pileupParams, hideLabels: pileup.hideLabels },
      hiddenCount: pileup.hiddenCount,
    };
  }

  buildHitPileup(hits: BlastHit[], queryRange: Range, pileupParams: IPileupParams): IPileup {
    const canvasWidth: number = Math.round(this.scale.range()[1]);
    const lanes: number[][] = [new Array(canvasWidth)]; // init with one empty lane
    const pileupHits: IPileupHit[] = [];

    hits.forEach((hit: BlastHit) => {
      const hitQueryRange: Range = getHitQueryRange(hit);

      if (rangesIntersect(hitQueryRange, queryRange)) {
        // we calculate the range we will draw the SVG elements to
        const hitRangeStart: number = Math.max(hitQueryRange.start, queryRange.start);
        const hitRangeStop: number = Math.min(hitQueryRange.stop + 1, queryRange.stop);

        // calculate the drawing parameters
        const x: number = Math.round(this.scalePos(hitRangeStart));
        const width: number = Math.max(
          Math.round(this.scalePos(hitRangeStop + 1) - this.scalePos(hitRangeStart)),
          pileupParams.minShapeWidth,
        );

        // to calculate the pileup
        const labelWidth: number = getHitName(hit).length * pileupParams.charWidthInPixel;

        // if the label is on top , we choose the max of both shape and label
        // also, if the label is larger than the shape because of the canvas cut, we don't want the lanes to get messed up, we won't encounter the label
        const x2: number = pileupParams.labelHeight > 0 ? Math.max(x + width, x + labelWidth) : x + width;

        // try to pileup the hit into an existing lane without collisions
        const lane = placeOnLaneWithPileup(hit.rank, x, x2, lanes, canvasWidth, pileupParams.maxLanes);

        // keep track which query positions being mapped by which HSPs
        const mappedPositions: number[] = repeat(canvasWidth, 0);

        // example array for hit with three HSPs: 000000111111100000002222222222222222223333330000000
        for (let i = 0; i < hit.hsps.length; i++) {
          const hsp: BlastHsp = hit.hsps[i];

          const hspRange: Range = getHspQueryRange(hsp);
          const viewStart: number = Math.min(Math.round(this.scalePos(hspRange.start)), canvasWidth - 1);
          const viewStop: number = Math.min(Math.round(this.scalePos(hspRange.stop + 1)), canvasWidth - 1);

          // Assign the HSP with the highest priority rank to all free locations (i.e. with the lowest rank number)
          //  The sequence of ranks can be gappy!
          //
          for (let j: number = viewStart; j <= viewStop; j++) {
            if (!mappedPositions[j]) {
              mappedPositions[j] = i + 1; // use +1 because 'unassigned' is 0
            }
          }
        }

        // calculate non-overlapping stretches of HSP mappings in query-position order
        const mappings: IPileupHspMapping[] = [];
        for (let s = 0; s < canvasWidth; s++) {
          const hspNum: number = mappedPositions[s];
          if (hspNum) {
            const hsp: BlastHsp = hit.hsps[hspNum - 1];
            // scan forward on canvas until the next hsp/rank is found
            let e: number = s + 1;
            for (; e < canvasWidth; e++) {
              if (mappedPositions[e] !== hspNum) {
                break;
              }
            }

            const hitStrand: StrandType = (
              hsp.targetStart <= hsp.targetStop ? STRAND_TYPE.PLUS : STRAND_TYPE.MINUS
            ) as StrandType;
            const queryStrand: StrandType = (
              hsp.queryStart <= hsp.queryStop ? STRAND_TYPE.PLUS : STRAND_TYPE.MINUS
            ) as StrandType;
            mappings.push({
              x: s - x,
              width: Math.max(e - s, pileupParams.minShapeWidth),
              start: undefined,
              stop: undefined,
              hsp,
              hitStrand,
              queryStrand,
            });

            s = e - 1;
          }
        }

        // calculate y coordinate based on lane
        const y: number =
          lane * (pileupParams.featureHeight + pileupParams.lanePadding) + (lane + 1) * pileupParams.labelHeight;

        pileupHits.push({ x, y, width, hit, mappings, lane });
      }
    });

    return {
      hits: pileupHits,
      lanes: lanes.length,
      pileupParams: pileupParams,
    };
  }

  buildHspPileup(hit: BlastHit, currentRange: Range, pileupParams: IPileupParams): IPileup {
    let canvasWidth: number = Math.round(this.scale.range()[1]);
    if (canvasWidth < 0) {
      canvasWidth = 0; // in case the canvas has not been fully initialized yet
    }
    const lanes: number[][] = [new Array(canvasWidth)]; // init with one empty lane
    const pileupHsps: IPileupHsp[] = [];

    hit.hsps.forEach((hsp: BlastHsp) => {
      const hspRange = getHspHitRange(hsp);

      if (rangesIntersect(hspRange, currentRange)) {
        // we calculate the range we will draw the SVG elements to
        // range of feature restricted to current range in model coords
        const featureRangeStart: number = Math.max(hspRange.start, currentRange.start);
        const featureRangeStop: number = Math.min(hspRange.stop, currentRange.stop);

        // give the information if the feature is cut so that we can adapt our drawing
        const startCut: boolean = featureRangeStart > hspRange.start;
        const endCut: boolean = featureRangeStop < hspRange.stop;

        // calculate the drawing parameters in canvas coords
        const x: number = Math.round(this.scalePos(featureRangeStart));
        const width: number = Math.max(
          Math.round(this.scalePos(featureRangeStop + 1) - this.scalePos(featureRangeStart)),
          pileupParams.minShapeWidth,
        );
        const labelWidth: number = getHspName(hit, hsp).length * pileupParams.charWidthInPixel;

        // range covered by this hsp is  [x ... x2)
        const x2: number = Math.max(x + width, x + labelWidth);

        // find a lane which has space left for the new range
        //   returns -1 if there is no lane and we cannot add another lane
        const lane = placeOnLaneWithPileup(hit.rank, x, x2, lanes, canvasWidth, pileupParams.maxLanes);

        if (lane !== -1) {
          // calculate y coordinate based on lane
          const y: number =
            lane * (pileupParams.featureHeight + pileupParams.lanePadding) + (lane + 1) * pileupParams.labelHeight;
          const hitStrand: StrandType = (
            hsp.targetStart <= hsp.targetStop ? STRAND_TYPE.PLUS : STRAND_TYPE.MINUS
          ) as StrandType;
          const queryStrand: StrandType = (
            hsp.queryStart <= hsp.queryStop ? STRAND_TYPE.PLUS : STRAND_TYPE.MINUS
          ) as StrandType;

          pileupHsps.push({ x, y, width, lane, hitStrand, queryStrand, startCut, endCut, hit, hsp });
        }
      }
    });

    return {
      hsps: pileupHsps,
      lanes: lanes.length,
      pileupParams: pileupParams,
    };
  }

  private scalePos(pos: number): number {
    return this.scale(pos - 1);
  }
}

function placeOnLaneWithPileup(
  mark: number,
  x: number,
  x2: number,
  lanes: number[][],
  canvasWidth: number,
  maxLanes: number = null,
): number {
  // Try to pileup the feature into an existing lane without collisions.
  //   Uses the lane with the lowest index with space for the entire feature.
  //   Returns the index of a lane which has space left for the new range.
  //   Returns -1 if there is no lane and we cannot add another lane.
  //   Does the bookkeeping for the lanes structure.

  let lane = -1;
  for (let i = 0; i < lanes.length; i++) {
    let fits = true;

    // test if positions [x,x2) of lane i are free
    for (let j: number = x; j < x2; j++) {
      if (lanes[i][j]) {
        fits = false;
        break;
      }
    }

    // mark positions [x,x2) as reserved for hit num
    if (fits) {
      for (let y = x; y < x2; y++) {
        lanes[i] = update(y, mark, lanes[i]);
      }
      lane = i;
      break;
    }
  }

  // limit the number of lanes
  if (lane < 0 && maxLanes && lanes.length >= maxLanes) {
    return -1;
  }

  // if hit doesn't fit anywhere, create a new lane and place hit in it
  if (lane < 0) {
    lane = lanes.length;
    let newLane: number[] = new Array(canvasWidth);
    for (let y = x; y < x2; y++) {
      newLane = update(y, mark, newLane);
    }
    lanes.push(newLane);
  }

  return lane;
}

function compareByStartThenLongestFirst(f1: Feature | RegionFlag, f2: Feature | RegionFlag): number {
  if (f1.region.range.start !== f2.region.range.start) {
    return f1.region.range.start < f2.region.range.start ? -1 : 1;
  } else if (f1.region.range.stop !== f2.region.range.stop) {
    return f1.region.range.stop < f2.region.range.stop ? 1 : -1;
  } else {
    return 0;
  }
}

function compareByLaneThenStart(e1: IPileupVariant, e2: IPileupVariant): number {
  if (e1.lane !== e2.lane) {
    return e1.lane < e2.lane ? -1 : 1;
  } else if (e1.variants[0].region.range.start !== e2.variants[0].region.range.start) {
    return e1.variants[0].region.range.start < e2.variants[0].region.range.start ? -1 : 1;
  } else {
    return 0;
  }
}

function compareByMidpointThenLaneThenStart(e1: IPileupVariant, e2: IPileupVariant): number {
  if (e1.midpoint !== e2.midpoint) {
    return e1.midpoint < e2.midpoint ? -1 : 1;
  } else {
    return compareByLaneThenStart(e1, e2);
  }
}

function compareByIconStartAndByWidestFirstAndCount(f1: IGroupFingerprint, f2: IGroupFingerprint): number {
  if (f1.x1 !== f2.x1) {
    return f1.x1 < f2.x1 ? -1 : 1;
  } else if (f1.x2 !== f2.x2) {
    // the longer range is better!
    return f1.x2 < f2.x2 ? 1 : -1;
  } else if (f1.count !== f2.count) {
    return f1.count < f2.count ? -1 : 1;
  } else {
    return 0;
  }
}

function isFingerprintOverlap(a: IGroupFingerprint, b: IGroupFingerprint): boolean {
  const r1: Range = { start: a.x1, stop: a.x2 };
  const r2: Range = { start: b.x1, stop: b.x2 };
  return rangesIntersect(r1, r2);
}

function getAverageExpressionValue(rows: DataRow[], condition: string) {
  const allValues: number[] = [];
  // note that some rows may lack a certain condition...
  rows.forEach((r: DataRow) => {
    const stringMapEntry: StringMapEntry = r.values.find((rr: StringMapEntry) => rr.key === condition);
    if (stringMapEntry) {
      allValues.push(Number(stringMapEntry.value));
    }
  });
  return allValues.length > 0 ? mean(allValues) : null;
}

export function addExpressionColor(pileup: IPileup, expressionColoring: TrackExpressionColoring): IPileup {
  if (expressionColoring) {
    const colorInterpolation = interpolateRgbBasis(expressionColoring.colorScheme.colors);
    const minValue: number = expressionColoring.minValue;
    const maxValue: number = expressionColoring.maxValue;

    pileup.features.forEach((f: IPileupFeature) => {
      expressionColoring.conditions.forEach((condition: string) => {
        const expressionParams: IExpressionParams = { condition };
        if (f.feature.dataSeries) {
          const expressionValue: number = getAverageExpressionValue(f.feature.dataSeries.rows, condition);

          if (expressionValue) {
            let relativeExpressionValue: number;
            if (expressionColoring.logScale) {
              relativeExpressionValue =
                expressionValue <= 0
                  ? Math.log(minValue)
                  : (Math.log(expressionValue) - Math.log(minValue)) / (Math.log(maxValue) - Math.log(minValue));
            } else {
              relativeExpressionValue = (expressionValue - minValue) / (maxValue - minValue);
            }
            expressionParams.expressionColor = colorInterpolation(relativeExpressionValue);
            expressionParams.expressionValue = expressionValue;
          } else {
            expressionParams.expressionColor = null;
          }
        } else {
          expressionParams.expressionColor = null;
        }
        expressionParams.isExpressionColoring = true;
        f.expressionParams.push(expressionParams);
      });
    });
  }
  return pileup;
}
